import { useEffect, useMemo, useState } from 'react'

import { logger } from '~/debug/logger'
import { useIsCloaking } from '~/store/slices/currentOrg'

import { type DepotModel } from '../client/chargers'
import { usePolledSites } from '../hooks/sites'
import { type PollingContextValue, usePollers } from './polling'
import type { IndexedDbService } from './storage/IndexedDbService'
import type { SiteCacheRecord } from './storage/schema'

export type SitesDataManagerContextValue = PollingContextValue & {
  data: DepotModel[]
  isLoading: boolean
  isFetching: boolean
}

export default function useSiteDataManager(service: IndexedDbService) {
  const { addPoller, effectivePollingInterval } = usePollers()

  const { sites, isLoading, isFetching } = usePolledSites({
    options: {
      intervalSeconds: effectivePollingInterval
    }
  })

  const data = useSitePersistence(service, sites)

  return useMemo<SitesDataManagerContextValue>(
    () => ({
      data,
      isLoading,
      isFetching,
      addPoller
    }),
    [data, isLoading, isFetching, addPoller]
  )
}

function useSitePersistence(service: IndexedDbService, sites?: DepotModel[]): DepotModel[] {
  const [hydratedSites, setHydratedSites] = useState<DepotModel[]>([])
  const isCloaking = useIsCloaking()

  // An empty snapshot fetched while cloaking can't be trusted: the depots request keeps
  // the principal's JWT but carries the cloaked `X-Organization-Id`, and for Synop staff
  // that combination can come back empty — persisting it would wipe every option out of
  // Visible Sites (ENG-10439). Outside cloaking an empty response is authoritative and
  // replaces the cache as usual, so orgs that genuinely have no sites stop showing stale ones.
  const isUntrustedEmptySnapshot = isCloaking && sites?.length === 0

  // Hydrate the site cache from IndexedDB
  useEffect(() => {
    async function hydrateSiteCache() {
      try {
        const records = await service.getAll('sites')
        setHydratedSites(records.map((record) => record.payload))
      } catch (error) {
        logger.error('Failed to hydrate site cache', error)
      }
    }

    void hydrateSiteCache()
  }, [service])

  // Persist the sites to IndexedDB
  useEffect(() => {
    if (!sites || isUntrustedEmptySnapshot) return

    const fetchedAt = Date.now()
    const records: SiteCacheRecord[] = sites.map((site) => ({
      id: site.depotId,
      payload: site,
      syncedAt: fetchedAt
    }))

    const replaceRecords = async () => {
      try {
        await service.replace('sites', records)
      } catch (error) {
        logger.error('Failed to persist site snapshot', error)
      }
    }

    void replaceRecords()
  }, [sites, service, isUntrustedEmptySnapshot])

  return sites && !isUntrustedEmptySnapshot ? sites : hydratedSites
}
